import { format as dateFnsFormat } from 'date-fns'
import {
  enUS as en,
  es,
  fr,
  ko,
  pl,
  ptBR as pt,
  ru,
  zhCN as zh,
} from 'date-fns/locale'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { AvailableLocales } from '../locales'

type DateFunctions =
  | 'formatDateLocal'
  | 'formatDayOfMonth'
  | 'formatDayOfWeek'
  | 'formatISODate'
  | 'formatMonthLong'
  | 'formatMonthAndYearLong'
  | 'formatWeekOfYear'
  | 'formatYearAndMonth'

export type SupportedLocales = keyof AvailableLocales

const locales = {
  en,
  es,
  fr,
  ko,
  pl,
  pt,
  ru,
  zh,
}

export default function useDateFormatters(): Record<
  DateFunctions,
  (date: Date) => string
> {
  const { locale } = useIntl()

  const format = useCallback(
    (date: Date, format: string): string =>
      dateFnsFormat(date, format, {
        locale: locales[locale as SupportedLocales] || locales.en,
      }),
    [locale],
  )

  return useMemo(
    () => ({
      formatDateLocal: (date: Date) => format(date, 'PP'),
      formatDayOfMonth: (date: Date) => format(date, 'd'),
      formatDayOfWeek: (date: Date) => format(date, 'EEEEEE'),
      formatISODate: (date: Date) => format(date, 'yyyy-MM-dd'),
      formatMonthLong: (date: Date) => format(date, 'MMMM'),
      formatMonthAndYearLong: (date: Date) => format(date, 'MMMM yyyy'),
      formatWeekOfYear: (date: Date) => format(date, 'w'),
      formatYearAndMonth: (date: Date) => format(date, 'yyyy-MM'),
    }),
    [format],
  )
}
